import type * as React from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import type { Tenant } from '@etiquetachef/types';
import { matchesSearch } from '@/lib/firestoreTenants';
import { countryFlag } from '@/lib/format';
import { StatusBadge } from './StatusBadge';

const PAGE_SIZE = 10;

type Props = {
  tenants: Tenant[];
  loading: boolean;
  /** Destino ao clicar numa empresa (ex.: `/billing/invoices/${id}`). */
  getHref: (tenantId: string) => string;
  /** Título da tabela (ex.: "Selecione uma empresa"). */
  title: string;
};

/**
 * Tabela de seleção de empresa — mesmo padrão do `/tenants` (Cloudscape
 * `Table` + `useCollection`: busca, ordenação por nome, paginação). Clicar na
 * empresa navega para `getHref(id)`. Reutilizável (Faturas, Pagamentos, …) em
 * vez de um `Select`/dropdown quando há muitas empresas.
 *
 * Usado por: `routes/InvoicesPage.tsx` (hoje o único consumidor).
 */
export function TenantPickerTable({
  tenants,
  loading,
  getHref,
  title,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'name',
        header: t('tenants.columns.name'),
        sortingComparator: (a: Tenant, b: Tenant): number => a.name.localeCompare(b.name),
        cell: (tn: Tenant): React.ReactNode => (
          <div>
            <Link
              href={getHref(tn.id)}
              onFollow={(e): void => {
                e.preventDefault();
                void navigate(getHref(tn.id));
              }}
            >
              {tn.name}
            </Link>
            <Box fontSize="body-s" color="text-status-inactive" fontWeight="normal">
              <span style={{ fontFamily: 'monospace' }}>{tn.id}</span>
            </Box>
          </div>
        ),
      },
      {
        id: 'status',
        header: t('tenants.columns.status'),
        cell: (tn: Tenant): React.ReactNode => <StatusBadge status={tn.status} />,
      },
      {
        id: 'country',
        header: t('tenants.columns.country'),
        cell: (tn: Tenant): React.ReactNode =>
          tn.countryCode ? (
            <span>
              <span aria-hidden="true">{countryFlag(tn.countryCode)}</span>{' '}
              <span style={{ fontFamily: 'monospace' }}>{tn.countryCode}</span>
            </span>
          ) : (
            '—'
          ),
      },
    ],
    [t, navigate, getHref],
  );

  const base = useMemo(
    () => tenants.filter((tn) => tn.status !== 'DELETED'),
    [tenants],
  );

  const { items, collectionProps, filterProps, paginationProps, filteredItemsCount } =
    useCollection(base, {
      filtering: { filteringFunction: (tn, text) => matchesSearch(tn, text) },
      sorting: {},
      pagination: { pageSize: PAGE_SIZE },
    });

  return (
    <Table<Tenant>
      {...collectionProps}
      items={items}
      columnDefinitions={columnDefinitions}
      loading={loading}
      loadingText={t('common.loading', { defaultValue: 'Carregando…' })}
      trackBy="id"
      variant="container"
      empty={<Box textAlign="center" color="inherit">{t('tenants.empty')}</Box>}
      header={
        <Header counter={`(${String(filteredItemsCount ?? base.length)})`}>{title}</Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder={t('tenants.filters.searchPlaceholder')}
          filteringAriaLabel={t('common.search')}
          countText={t('tenants.resultCount', { count: filteredItemsCount ?? 0 })}
        />
      }
      pagination={<Pagination {...paginationProps} />}
    />
  );
}
